// Shared health definitions; values and helpers are maintained here.
import { STATUS, CHROME } from './palette';


// --- Node freshness / state --------------------------------------------
// State is a RING treatment, independent of role colour and protocol shape
// (spec §2). Thresholds are a phase-1 judgment call (spec doesn't pin exact
// numbers) — documented in the build report, tune later if it doesn't match
// reality. Protocol-agnostic: freshness means the same thing everywhere.
export type NodeFreshness = "online" | "stale" | "offline";


export const FRESHNESS_ONLINE_MS = 10 * 60 * 1000;
 // < 10 min: bright ring
export const FRESHNESS_STALE_MS = 60 * 60 * 1000;
 // < 60 min: faded ring; beyond: no ring

export function freshnessOf(lastHeard: string | number | null | undefined): NodeFreshness {
  if (!lastHeard) return "offline";
  const t = typeof lastHeard === "number" ? lastHeard * (lastHeard < 1e12 ? 1000 : 1) : Date.parse(lastHeard);
  if (Number.isNaN(t)) return "offline";
  const age = Date.now() - t;
  if (age < FRESHNESS_ONLINE_MS) return "online";
  if (age < FRESHNESS_STALE_MS) return "stale";
  return "offline";
}


export const FRESHNESS_RING_OPACITY: Record<NodeFreshness, number> = {
  online: 1,
  stale: 0.35,
  offline: 0,
};


export const FRESHNESS_LABEL: Record<NodeFreshness, string> = {
  online: "Online",
  stale: "Stale",
  offline: "Offline",
};


export const FRESHNESS_ORDER: NodeFreshness[] = ["online", "stale", "offline"];


// --- MQTT viewer (phase 3 backend, frontend built 2026-09-20) --------------
// Project scope: bring over the equivalent MeshMonitor features — the
// MQTT viewer, the sim nodes, all of it. The
// backend shipped in phase 3; this is the first real UI for it. Built
// directly against the running backend's actual response shapes (pulled
// live from the running instance, not a written spec) — GET /api/mqtt/brokers,
// /api/mqtt/gateways, and /api/mqtt/topics each return a bare ARRAY;
// /api/mqtt/undecryptable returns an OBJECT ({by_reason, recent}). That
// inconsistency is real and deliberately not smoothed over anywhere below.
// Generalized 2026-09-20 (was MqttConnectionState/mqttConnectionStateInfo,
// MQTT-only) once the Health page needed the exact same vocabulary for
// generic transports (/api/health) and observers (/api/observers) —
// "connected"/"connecting"/"disconnected"/"error" isn't an MQTT concept,
// it's just what a connection state is, and MQTT brokers were simply the
// first place this app read one.
export type ConnectionState = "connected" | "connecting" | "disconnected" | "error";


export interface ConnectionStateInfo {
  label: string;
  color: { light: string; dark: string };
}


export const CONNECTION_STATE_INFO: Record<ConnectionState, ConnectionStateInfo> = {
  connected: { label: "Connected", color: STATUS.good },
  connecting: { label: "Connecting", color: STATUS.warning },
  disconnected: { label: "Disconnected", color: STATUS.serious },
  error: { label: "Error", color: STATUS.critical },
};


export function isConnectionState(x: string | null | undefined): x is ConnectionState {
  return x === "connected" || x === "connecting" || x === "disconnected" || x === "error";
}


// An unrecognised state (the vocabulary above is only what's been seen
// live) still renders honestly — the raw backend string, greyed rather
// than guessed into one of the four known colours.
export function connectionStateInfo(raw: string | null | undefined): ConnectionStateInfo {
  return isConnectionState(raw) ? CONNECTION_STATE_INFO[raw] : { label: raw || "Unknown", color: CHROME.inkMuted };
}


// --- Liveness (Health page, 2026-09-20) ------------------------------------
// Design framing: stale-but-connected is the failure mode that
// matters — a transport can report connected while its last_packet_at is
// hours old, which is exactly a scenario MeshCore hits. Connected
// alone is a lying success flag, and this whole platform exists partly to
// catch those. This combines a transport/observer/broker's own
// connection_state with the recency of its last_packet_at (reusing
// freshnessOf()/FRESHNESS_*_MS — the same "how recent counts as fresh"
// definition already used for node liveness, not a second threshold
// invented for this page) into one severity: connected+fresh is the only
// "healthy" case, and connected+stale is deliberately its own visibly
// different colour rather than inheriting "connected"'s green, precisely
// so a lying success flag can't hide behind it.
export type LivenessSeverity = "healthy" | "stale" | "unhealthy";


export const LIVENESS_COLOR: Record<LivenessSeverity, { light: string; dark: string }> = {
  healthy: STATUS.good,
  stale: STATUS.warning,
  unhealthy: STATUS.critical,
};


export function livenessSeverity(connectionState: string | null | undefined, lastHeard: string | number | null | undefined): LivenessSeverity {
  if (connectionState !== "connected") return "unhealthy";
  const fresh = freshnessOf(lastHeard);
  if (fresh === "online") return "healthy";
  if (fresh === "stale") return "stale";
  return "unhealthy"; // connected, but offline-stale (or never heard at all) — the lying-flag case
}


// Decode-health severity for a channel/topic (undecryptable sample count
// against message volume) — the signal this page needs to lead with:
// a real case had 290 messages on one channel all failing to decrypt while
// only 3 of 79 decoded overall — the page should make that legible rather
// than making someone infer it from four separate tables. Graded rather than a
// single hard "missing key" claim, since the ratio is computed from a
// capped recent-sample (the API returns at most the newest 100
// undecryptable records, not the true lifetime total per channel).
export type DecodeHealthSeverity = "ok" | "warning" | "critical";


export const DECODE_HEALTH_COLOR: Record<DecodeHealthSeverity, { light: string; dark: string }> = {
  ok: STATUS.good,
  warning: STATUS.warning,
  critical: STATUS.critical,
};


export function decodeHealthSeverity(undecryptableSample: number, messageVolume: number): DecodeHealthSeverity {
  if (undecryptableSample <= 0) return "ok";
  const denominator = Math.max(messageVolume, undecryptableSample, 1);
  const ratio = undecryptableSample / denominator;
  if (ratio >= 0.6) return "critical";
  if (ratio >= 0.15) return "warning";
  return "ok";
}


// --- Global-data-layer stale indicator (data-layer slice 2, 2026-09-30) ---
// Owner's decision: show NOTHING while the app's global data (the header
// counts, Live Map, packet feed, node list — everything the persisted
// query cache backs, see state/AppData.tsx) is younger than 2x the
// backend's own `audit.recompute_interval_minutes` setting (default 15,
// so 30 minutes by default — NEVER hard-coded here, always derived from
// the live setting, read off GET /api/audit/recompute_status'
// recompute_interval_minutes; that endpoint is reused rather than adding a
// new one, since it's the one place this interval is already exposed —
// this reads the number only, nothing about the Analysis/Audit tabs' own
// recompute data). Only past that threshold does ONE small amber note
// appear in the header, reusing the exact plain-text, no-box, amber style
// BackboneTab's own backboneStaleWarning/.backbone-stale-warning already
// established for "something's off" text — not a new visual pattern.
export function staleDataNote(newestDataAtMs: number, recomputeIntervalMinutes: number): string | null {
  if (!newestDataAtMs) return null; // no data loaded at all yet — not a staleness question, see the loading-skeleton rule instead
  const staleAfterMs = 2 * recomputeIntervalMinutes * 60_000;
  if (Date.now() - newestDataAtMs <= staleAfterMs) return null;
  const d = new Date(newestDataAtMs);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `data from ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
